import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Avatar,
  LinearProgress,
  IconButton,
  Button,
  Badge,
  Collapse,
  Fade,
  Container,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  Rating,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  School,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Star,
  Download,
  Print,
  Share,
  Grade,
  EmojiEvents,
  CheckCircle,
  Error,
  Warning,
  Info,
  CalendarToday,
  Person,
  Schedule,
  Assessment,
  Psychology,
  Lightbulb,
  Bookmarks,
  LocalLibrary,
  SportsSoccer,
  MusicNote,
  ColorLens,
  Science,
  MenuBook,
  AccountTree,
  Group,
  Chat,
  ExpandMore,
  ExpandLess,
  ThumbUp,
  Visibility,
  AccessTime,
  AttachMoney,
  GppGood,
  WorkspacePremium,
  Timeline,
  AutoGraph,
  PieChart,
  BarChart,
  DonutLarge,
  Numbers,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";

// Floating animation
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
  100% { transform: translateY(0px); }
`;

const ParentReportCard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [expandedSubject, setExpandedSubject] = useState(null);
  const [shareDialog, setShareDialog] = useState(false);

  const studentData = {
    name: "Arun Kumar",
    class: "Grade 3 - B",
    rollNo: "STU2025001",
    avatar: "https://cdn-icons-png.flaticon.com/512/4140/4140047.png",
    academicYear: "2024-2025",
    term: "Term 2",
    reportDate: "2025-02-20",
    teacher: "Ms. Priya Sharma",
    principal: "Dr. Ramesh Kumar",
  };

  const overallPerformance = {
    overallScore: 84,
    attendance: 92,
    behavior: 4.5,
    rank: 5,
    totalStudents: 42,
    improvement: "+8%",
    grade: "A",
    status: "Excellent",
  };

  const subjects = [
    {
      id: "math",
      name: "Mathematics",
      score: 84,
      grade: "A",
      color: "#4CAF50",
      icon: <Numbers />,
      trend: "up",
      teacherComment: "Shows excellent problem-solving skills. Strong grasp of multiplication and division concepts. Needs more practice with word problems.",
      assignments: 12,
      tests: 4,
      projects: 2,
      skills: ["Problem Solving", "Logical Thinking", "Calculations"],
      improvement: "+5%",
    },
    {
      id: "science",
      name: "Science",
      score: 76,
      grade: "B+",
      color: "#7B61FF",
      icon: <Science />,
      trend: "up",
      teacherComment: "Very curious and observant. Excellent performance in practical experiments. Can improve on theoretical concepts.",
      assignments: 10,
      tests: 3,
      projects: 3,
      skills: ["Observation", "Experiment Design", "Scientific Method"],
      improvement: "+3%",
    },
    {
      id: "english",
      name: "English Language",
      score: 90,
      grade: "A+",
      color: "#1976D2",
      icon: <MenuBook />,
      trend: "stable",
      teacherComment: "Excellent reading comprehension and vocabulary. Creative writing shows great imagination. Pronunciation is very clear.",
      assignments: 15,
      tests: 5,
      projects: 1,
      skills: ["Reading", "Writing", "Speaking"],
      improvement: "+2%",
    },
    {
      id: "social",
      name: "Social Studies",
      score: 82,
      grade: "A",
      color: "#FFB300",
      icon: <AccountTree />,
      trend: "up",
      teacherComment: "Shows great interest in history and geography. Good understanding of cultural diversity. Can work on map skills.",
      assignments: 8,
      tests: 3,
      projects: 2,
      skills: ["Historical Understanding", "Cultural Awareness", "Geographical Knowledge"],
      improvement: "+7%",
    },
    {
      id: "art",
      name: "Art & Creativity",
      score: 95,
      grade: "A+",
      color: "#FF6B6B",
      icon: <ColorLens />,
      trend: "up",
      teacherComment: "Exceptional creativity and artistic expression. Excellent color sense and composition. Very imaginative.",
      assignments: 6,
      tests: 2,
      projects: 4,
      skills: ["Creativity", "Fine Motor Skills", "Color Theory"],
      improvement: "+4%",
    },
    {
      id: "music",
      name: "Music",
      score: 88,
      grade: "A",
      color: "#4ECDC4",
      icon: <MusicNote />,
      trend: "stable",
      teacherComment: "Good sense of rhythm and melody. Enjoys participating in group performances. Can work on instrument techniques.",
      assignments: 5,
      tests: 2,
      projects: 2,
      skills: ["Rhythm", "Pitch", "Team Performance"],
      improvement: "+1%",
    },
    {
      id: "pe",
      name: "Physical Education",
      score: 92,
      grade: "A+",
      color: "#FFD166",
      icon: <SportsSoccer />,
      trend: "up",
      teacherComment: "Excellent sportsmanship and teamwork. Good physical fitness and coordination. Natural leadership in team sports.",
      assignments: 8,
      tests: 3,
      projects: 3,
      skills: ["Teamwork", "Coordination", "Sportsmanship"],
      improvement: "+6%",
    },
    {
      id: "life",
      name: "Practical Life Skills",
      score: 80,
      grade: "B+",
      color: "#9C27B0",
      icon: <Psychology />,
      trend: "up",
      teacherComment: "Shows good independence and responsibility. Fine motor skills improving. Can work on organizational skills.",
      assignments: 10,
      tests: 2,
      projects: 3,
      skills: ["Independence", "Organization", "Responsibility"],
      improvement: "+9%",
    },
  ];

  const skillCategories = [
    {
      category: "Academic Skills",
      skills: [
        { name: "Problem Solving", score: 85, trend: "up" },
        { name: "Critical Thinking", score: 78, trend: "up" },
        { name: "Memory Retention", score: 82, trend: "stable" },
        { name: "Time Management", score: 70, trend: "up" },
      ],
    },
    {
      category: "Social Skills",
      skills: [
        { name: "Teamwork", score: 90, trend: "up" },
        { name: "Communication", score: 85, trend: "up" },
        { name: "Leadership", score: 88, trend: "up" },
        { name: "Empathy", score: 82, trend: "stable" },
      ],
    },
    {
      category: "Personal Development",
      skills: [
        { name: "Creativity", score: 95, trend: "up" },
        { name: "Curiosity", score: 88, trend: "up" },
        { name: "Resilience", score: 75, trend: "up" },
        { name: "Focus", score: 72, trend: "stable" },
      ],
    },
  ];

  const assessments = [
    { type: "Term Test 1", date: "2025-01-15", score: 82, max: 100, weight: 30 },
    { type: "Term Test 2", date: "2025-02-10", score: 88, max: 100, weight: 30 },
    { type: "Project Work", date: "2025-02-05", score: 92, max: 100, weight: 20 },
    { type: "Class Participation", date: "Continuous", score: 95, max: 100, weight: 10 },
    { type: "Homework", date: "Continuous", score: 90, max: 100, weight: 10 },
  ];

  const achievements = [
    { title: "Math Olympiad Winner", date: "Jan 2025", icon: "🏆", category: "Academic" },
    { title: "Perfect Attendance", date: "Dec 2024", icon: "⭐", category: "Discipline" },
    { title: "Art Competition 1st Prize", date: "Feb 2025", icon: "🎨", category: "Creative" },
    { title: "Sports Day Champion", date: "Jan 2025", icon: "⚽", category: "Sports" },
    { title: "Science Fair Excellence", date: "Feb 2025", icon: "🔬", category: "Academic" },
  ];

  const recommendations = [
    { action: "Practice word problems daily", priority: "High", subject: "Mathematics" },
    { action: "Read one chapter book per week", priority: "Medium", subject: "English" },
    { action: "Join science club activities", priority: "Low", subject: "Science" },
    { action: "Work on organizational skills", priority: "Medium", subject: "Life Skills" },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleExpand = (subjectId) => {
    setExpandedSubject(expandedSubject === subjectId ? null : subjectId);
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "up": return <TrendingUp sx={{ fontSize: 16, color: "#4CAF50" }} />;
      case "down": return <TrendingDown sx={{ fontSize: 16, color: "#F44336" }} />;
      default: return <TrendingFlat sx={{ fontSize: 16, color: "#FFB300" }} />;
    }
  };

  const getGradeColor = (grade) => {
    if (grade.includes('A+') || grade.includes('A')) return "#4CAF50";
    if (grade.includes('B')) return "#FFB300";
    if (grade.includes('C')) return "#FF9800";
    if (grade.includes('D')) return "#F44336";
    return "#666";
  };

  const calculateOverallAverage = () => {
    return subjects.reduce((sum, subject) => sum + subject.score, 0) / subjects.length;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        backgroundImage: `
          linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)),
          url('https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1920')
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: "none",
        },
      }}
    >
      {/* Decorative elements */}
      <Box
        sx={{
          position: "absolute",
          top: 100,
          right: 100,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(120, 119, 198, 0.05) 0%, transparent 70%)",
          filter: "blur(40px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 100,
          left: 100,
          width: 150,
          height: 150,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255, 119, 198, 0.05) 0%, transparent 70%)",
          filter: "blur(30px)",
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* HEADER */}
        <Fade in timeout={800}>
          <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#333",
                    mb: 1,
                    fontSize: { xs: "1.75rem", md: "2.125rem" },
                  }}
                >
                  Academic Report Card
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: 400 }}>
                  {studentData.name} • {studentData.class} • {studentData.academicYear} • {studentData.term}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  startIcon={<Download />}
                  variant="contained"
                  sx={{
                    bgcolor: "#1976D2",
                    borderRadius: "25px",
                    px: 3,
                    py: 1,
                    fontWeight: 500,
                    "&:hover": {
                      bgcolor: "#1565C0",
                    },
                  }}
                >
                  Download PDF
                </Button>
                <Button
                  startIcon={<Print />}
                  variant="outlined"
                  sx={{
                    borderRadius: "25px",
                    px: 3,
                    py: 1,
                    borderColor: "rgba(0, 0, 0, 0.23)",
                    color: "#666",
                    fontWeight: 500,
                  }}
                >
                  Print
                </Button>
                <Button
                  startIcon={<Share />}
                  variant="outlined"
                  sx={{
                    borderRadius: "25px",
                    px: 3,
                    py: 1,
                    borderColor: "rgba(0, 0, 0, 0.23)",
                    color: "#666",
                    fontWeight: 500,
                  }}
                  onClick={() => setShareDialog(true)}
                >
                  Share
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>

        {/* STUDENT PROFILE */}
        <Fade in timeout={900}>
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              mx: { xs: 2, md: 4 },
              p: { xs: 3, md: 4 },
              borderRadius: "20px",
              background: "rgba(255, 255, 255, 0.7)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              boxShadow: "0 8px 32px rgba(31, 38, 135, 0.1)",
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={3} sx={{ textAlign: "center" }}>
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  <Avatar
                    src={studentData.avatar}
                    sx={{
                      width: 120,
                      height: 120,
                      border: "4px solid white",
                      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                    }}
                  />
                  <Chip
                    label={overallPerformance.grade}
                    size="small"
                    sx={{
                      position: "absolute",
                      bottom: 10,
                      right: -10,
                      bgcolor: getGradeColor(overallPerformance.grade),
                      color: "white",
                      fontWeight: 700,
                      fontSize: "0.9rem",
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: "#1a1a1a" }}>
                      {studentData.name}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#666", mb: 2 }}>
                      <School sx={{ fontSize: 16, verticalAlign: "middle", mr: 1 }} />
                      {studentData.class} • Roll No: {studentData.rollNo}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                      <Chip 
                        label={`Overall Score: ${overallPerformance.overallScore}%`} 
                        color="primary" 
                        icon={<Grade />}
                      />
                      <Chip 
                        label={`Rank: ${overallPerformance.rank}/${overallPerformance.totalStudents}`} 
                        color="success" 
                        icon={<EmojiEvents />}
                      />
                      <Chip 
                        label={overallPerformance.status} 
                        color="warning" 
                        icon={<Star />}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ color: "#666" }}>Class Teacher:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{studentData.teacher}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ color: "#666" }}>Report Date:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {new Date(studentData.reportDate).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ color: "#666" }}>Attendance:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#4CAF50" }}>
                          {overallPerformance.attendance}%
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ color: "#666" }}>Behavior Score:</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Rating value={overallPerformance.behavior} precision={0.5} size="small" readOnly />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{overallPerformance.behavior}/5</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Fade>

        {/* PERFORMANCE OVERVIEW CARDS */}
        <Fade in timeout={1000}>
          <Grid container spacing={3} sx={{ mb: 4, px: { xs: 2, md: 4 } }}>
            <Grid item xs={6} sm={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.8)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 900, color: "#1976D2", mb: 1 }}>
                  {overallPerformance.overallScore}%
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
                  Overall Score
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mt: 1 }}>
                  <TrendingUp sx={{ fontSize: 16, color: "#4CAF50" }} />
                  <Typography variant="caption" sx={{ color: "#4CAF50" }}>
                    {overallPerformance.improvement}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.8)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 900, color: "#4CAF50", mb: 1 }}>
                  {overallPerformance.grade}
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
                  Final Grade
                </Typography>
                <Typography variant="caption" sx={{ color: "#666", display: "block", mt: 1 }}>
                  {overallPerformance.status}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.8)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 900, color: "#FFB300", mb: 1 }}>
                  {overallPerformance.rank}
                  <Typography variant="caption" sx={{ color: "#666", ml: 0.5 }}>
                    /{overallPerformance.totalStudents}
                  </Typography>
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
                  Class Rank
                </Typography>
                <Chip
                  label="Top 10%"
                  size="small"
                  sx={{
                    mt: 1,
                    bgcolor: alpha("#FFB300", 0.1),
                    color: "#FFB300",
                    fontWeight: 500,
                  }}
                />
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.8)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 900, color: "#7B61FF", mb: 1 }}>
                  {subjects.length}
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
                  Subjects
                </Typography>
                <Typography variant="caption" sx={{ color: "#666", display: "block", mt: 1 }}>
                  {subjects.filter(s => s.score >= 80).length} above 80%
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Fade>

        {/* TABS FOR DIFFERENT VIEWS */}
        <Fade in timeout={1100}>
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              mx: { xs: 2, md: 4 },
              borderRadius: "20px",
              background: "rgba(255, 255, 255, 0.7)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              overflow: "hidden",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                px: 3,
                pt: 2,
              }}
            >
              <Tab label="Subject Performance" icon={<LocalLibrary />} iconPosition="start" />
              <Tab label="Skills Assessment" icon={<Assessment />} iconPosition="start" />
              <Tab label="Detailed Assessments" icon={<Timeline />} iconPosition="start" />
              <Tab label="Achievements" icon={<EmojiEvents />} iconPosition="start" />
            </Tabs>

            {/* TAB 1: SUBJECT PERFORMANCE */}
            {activeTab === 0 && (
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {subjects.map((subject) => (
                    <Grid item xs={12} md={6} key={subject.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: "16px",
                          background: "rgba(255, 255, 255, 0.6)",
                          backdropFilter: "blur(10px)",
                          border: `1px solid ${alpha(subject.color, 0.2)}`,
                          borderLeft: `4px solid ${subject.color}`,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "12px",
                                bgcolor: alpha(subject.color, 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: subject.color,
                                animation: `${floatAnimation} 2s ease-in-out infinite`,
                              }}
                            >
                              {subject.icon}
                            </Box>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
                                {subject.name}
                              </Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Chip
                                  label={subject.grade}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(getGradeColor(subject.grade), 0.1),
                                    color: getGradeColor(subject.grade),
                                    fontWeight: 700,
                                  }}
                                />
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  {getTrendIcon(subject.trend)}
                                  <Typography variant="caption" sx={{ color: "#666" }}>
                                    {subject.improvement}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                          <Typography variant="h4" sx={{ fontWeight: 900, color: subject.color }}>
                            {subject.score}%
                          </Typography>
                        </Box>

                        <LinearProgress
                          variant="determinate"
                          value={subject.score}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            mb: 2,
                            bgcolor: alpha(subject.color, 0.1),
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 4,
                              bgcolor: subject.color,
                            },
                          }}
                        />

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#666" }}>
                            Teacher's Comment:
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#444", fontStyle: "italic" }}>
                            "{subject.teacherComment}"
                          </Typography>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: subject.color }}>
                                {subject.assignments}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#666" }}>
                                Assignments
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: subject.color }}>
                                {subject.tests}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#666" }}>
                                Tests
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: subject.color }}>
                                {subject.projects}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#666" }}>
                                Projects
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        <Button
                          size="small"
                          endIcon={expandedSubject === subject.id ? <ExpandLess /> : <ExpandMore />}
                          onClick={() => handleExpand(subject.id)}
                          sx={{ mt: 2, color: subject.color }}
                        >
                          {expandedSubject === subject.id ? "Show Less" : "View Skills"}
                        </Button>

                        <Collapse in={expandedSubject === subject.id}>
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#666" }}>
                              Key Skills:
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {subject.skills.map((skill, index) => (
                                <Chip
                                  key={index}
                                  label={skill}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(subject.color, 0.1),
                                    color: subject.color,
                                    fontWeight: 500,
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </Collapse>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* TAB 2: SKILLS ASSESSMENT */}
            {activeTab === 1 && (
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {skillCategories.map((category, index) => (
                    <Grid item xs={12} md={4} key={index}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: "16px",
                          background: "rgba(255, 255, 255, 0.6)",
                          backdropFilter: "blur(10px)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          height: "100%",
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: "#1a1a1a" }}>
                          {category.category}
                        </Typography>
                        {category.skills.map((skill, idx) => (
                          <Box key={idx} sx={{ mb: 3 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: "#666" }}>
                                {skill.name}
                              </Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                {getTrendIcon(skill.trend)}
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {skill.score}%
                                </Typography>
                              </Box>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={skill.score}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: "rgba(0, 0, 0, 0.05)",
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 3,
                                  bgcolor: skill.score >= 80 ? "#4CAF50" : skill.score >= 60 ? "#FFB300" : "#F44336",
                                },
                              }}
                            />
                          </Box>
                        ))}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* TAB 3: DETAILED ASSESSMENTS */}
            {activeTab === 2 && (
              <Box sx={{ p: 3 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Assessment Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Score</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Max</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Weight</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Grade</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assessments.map((assessment, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {assessment.type}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {assessment.date === "Continuous" 
                                ? assessment.date 
                                : new Date(assessment.date).toLocaleDateString('en-IN', {
                                    day: 'numeric',
                                    month: 'short'
                                  })}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {assessment.score}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{assessment.max}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${assessment.weight}%`}
                              size="small"
                              sx={{
                                bgcolor: alpha("#1976D2", 0.1),
                                color: "#1976D2",
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={assessment.score >= 90 ? "A+" : assessment.score >= 80 ? "A" : assessment.score >= 70 ? "B+" : "B"}
                              size="small"
                              sx={{
                                bgcolor: alpha(assessment.score >= 80 ? "#4CAF50" : "#FFB300", 0.1),
                                color: assessment.score >= 80 ? "#4CAF50" : "#FFB300",
                                fontWeight: 700,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* TAB 4: ACHIEVEMENTS */}
            {activeTab === 3 && (
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {achievements.map((achievement, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card
                        sx={{
                          borderRadius: "16px",
                          background: "rgba(255, 255, 255, 0.6)",
                          backdropFilter: "blur(10px)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 12px 32px rgba(0,0,0,0.1)",
                          },
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                          <Typography variant="h2" sx={{ mb: 2 }}>
                            {achievement.icon}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: "#1a1a1a" }}>
                            {achievement.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                            {achievement.date}
                          </Typography>
                          <Chip
                            label={achievement.category}
                            size="small"
                            sx={{
                              bgcolor: alpha("#1976D2", 0.1),
                              color: "#1976D2",
                              fontWeight: 500,
                            }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Paper>
        </Fade>

        {/* RECOMMENDATIONS & NEXT STEPS */}
        <Fade in timeout={1200}>
          <Grid container spacing={3} sx={{ mb: 4, px: { xs: 2, md: 4 } }}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.7)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  height: "100%",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: "#1a1a1a", display: "flex", alignItems: "center", gap: 1 }}>
                  <Lightbulb sx={{ color: "#1976D2" }} /> Recommendations for Improvement
                </Typography>
                <List>
                  {recommendations.map((rec, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        p: 2,
                        mb: 1,
                        borderRadius: "12px",
                        bgcolor: alpha("#1976D2", 0.05),
                        borderLeft: `4px solid ${
                          rec.priority === "High" ? "#F44336" : 
                          rec.priority === "Medium" ? "#FFB300" : 
                          "#4CAF50"
                        }`,
                      }}
                    >
                      <ListItemIcon>
                        {rec.priority === "High" ? <Error sx={{ color: "#F44336" }} /> :
                         rec.priority === "Medium" ? <Warning sx={{ color: "#FFB300" }} /> :
                         <Info sx={{ color: "#4CAF50" }} />}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {rec.action}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: "#666" }}>
                            Subject: {rec.subject} • Priority: {rec.priority}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.7)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  height: "100%",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: "#1a1a1a", display: "flex", alignItems: "center", gap: 1 }}>
                  <WorkspacePremium sx={{ color: "#FFB300" }} /> Overall Performance Summary
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                    <strong>{studentData.name}</strong> has shown <strong>{overallPerformance.improvement} improvement</strong> this term and maintains a strong position in class. Areas of strength include creativity, teamwork, and academic performance in arts and sports. Focus areas for next term include word problems in mathematics and organizational skills.
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      Class Teacher:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {studentData.teacher}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      Principal:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {studentData.principal}
                    </Typography>
                  </Box>
                </Box>
                <Alert
                  severity="success"
                  sx={{
                    borderRadius: "12px",
                    bgcolor: alpha("#4CAF50", 0.05),
                  }}
                >
                  <AlertTitle>Excellent Progress!</AlertTitle>
                  Student is performing above class average in 6 out of 8 subjects. Keep up the great work!
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Fade>

        {/* SHARE DIALOG */}
        <Dialog
          open={shareDialog}
          onClose={() => setShareDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Share Report Card
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
              Share {studentData.name}'s report card with family members or save for your records.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button fullWidth variant="outlined" startIcon={<Share />}>
                  Email PDF
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button fullWidth variant="outlined" startIcon={<Share />}>
                  Generate Link
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShareDialog(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* FOOTER */}
        <Fade in timeout={1300}>
          <Box sx={{ py: 4, textAlign: "center", px: { xs: 2, md: 4 } }}>
            <Typography variant="body2" sx={{ color: "#666", opacity: 0.8 }}>
              © 2025 My School • Official Academic Record • Report generated on: {new Date().toLocaleDateString('en-IN')}
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default ParentReportCard;